import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { NAV, BRAND, PRODUCTS } from '../data/mock';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import Logo from './Logo';
import TopBar from './TopBar';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [topbarVisible, setTopbarVisible] = useState(true);
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 40);
      setTopbarVisible(y < 200);
    };
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); setServicesOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // eslint-disable-next-line no-unused-vars
  const isHomeAtTop = location.pathname === '/' && !scrolled;

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50">
        <div
          className="overflow-hidden transition-[max-height,opacity] duration-500 ease-out"
          style={{ maxHeight: topbarVisible ? 60 : 0, opacity: topbarVisible ? 1 : 0 }}
        >
          <TopBar />
        </div>
        <div
          className={`transition-all duration-500 ${scrolled ? 'bg-[#314F2B]/85 backdrop-blur-md border-b border-[#E7E9E4]/8' : 'bg-transparent'}`}
        >
          <div className="mx-auto px-5 md:px-10" style={{ maxWidth: '1800px' }}>
            <nav className="flex items-center justify-between py-1.5 md:py-2">
              <Link to="/" className="flex items-center gap-3 group" data-cursor="hover">
                <Logo size="lg" shimmer />
              </Link>

            <ul className="hidden lg:flex items-center gap-0.5">
              {NAV.map((item) => {
                const active = location.pathname === item.href || (item.href === '/products' && location.pathname === '/installation');
                if (item.dropdown) {
                  return (
                    <li
                      key={item.href}
                      className="relative"
                      onMouseEnter={() => setServicesOpen(true)}
                      onMouseLeave={() => setServicesOpen(false)}
                    >
                      <Link
                        to={item.href}
                        data-cursor="hover"
                        className={`relative px-4 py-2 text-[11px] tracking-[0.22em] uppercase font-semibold transition-colors duration-300 flex items-center gap-1.5 ${active ? 'text-[#6A8F3E]' : 'text-[#E7E9E4] hover:text-[#6A8F3E]'}`}
                      >
                        {item.label}
                        <ChevronDown size={11} strokeWidth={2.4} className={`transition-transform duration-300 ${servicesOpen ? 'rotate-180' : ''}`} />
                      </Link>
                      <div className={`absolute top-full left-1/2 -translate-x-1/2 pt-3 transition-all duration-300 ${servicesOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-2 pointer-events-none'}`}>
                        <div className="glass rounded-2xl p-2 min-w-[260px]">
                          {PRODUCTS.map((p) => (
                            <Link
                              key={p.slug}
                              to="/products"
                              data-cursor="hover"
                              className="flex items-center justify-between gap-4 px-4 py-3 rounded-xl hover:bg-[#6A8F3E]/15 group/item transition-colors"
                            >
                              <div>
                                <div className="text-[13px] font-semibold text-[#E7E9E4] group-hover/item:text-[#6A8F3E] transition-colors">{p.name}</div>
                                <div className="text-[10px] tracking-[0.18em] uppercase text-[#6A8F3E]/70 mt-0.5">{p.pile} pile</div>
                              </div>
                              <span className="text-[#6A8F3E] opacity-0 group-hover/item:opacity-100 -translate-x-1 group-hover/item:translate-x-0 transition-all">→</span>
                            </Link>
                          ))}
                          <div className="border-t border-[#E7E9E4]/8 mt-2 pt-2">
                            <Link to="/installation" data-cursor="hover" className="flex items-center justify-between gap-4 px-4 py-3 rounded-xl hover:bg-[#6A8F3E]/15 group/item transition-colors">
                              <div className="text-[13px] font-semibold text-[#E7E9E4] group-hover/item:text-[#6A8F3E] transition-colors">Our Installation Process</div>
                              <span className="text-[#6A8F3E] opacity-0 group-hover/item:opacity-100 -translate-x-1 group-hover/item:translate-x-0 transition-all">→</span>
                            </Link>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                }
                return (
                  <li key={item.href}>
                    <Link
                      to={item.href}
                      data-cursor="hover"
                      className={`relative px-4 py-2 text-[11px] tracking-[0.22em] uppercase font-semibold transition-colors duration-300 ${active ? 'text-[#A3C66F]' : 'text-[#E7E9E4] hover:text-[#A3C66F]'}`}
                    >
                      {item.label}
                      {active && <span className="absolute left-1/2 -translate-x-1/2 bottom-1 w-1 h-1 rounded-full bg-[#A3C66F]" />}
                    </Link>
                  </li>
                );
              })}
            </ul>

            <div className="flex items-center gap-2">
              <Link
                to="/contact"
                data-cursor="hover"
                data-testid="navbar-get-in-touch-btn"
                className="hidden md:inline-flex items-center gap-2 rounded-full bg-[#6A8F3E] hover:bg-[#7BA047] text-[#1A1A1A] text-[11px] tracking-[0.22em] uppercase font-extrabold px-5 py-1.5 transition-all duration-500 shadow-[0_4px_18px_-2px_rgba(106,143,62,0.5)]"
              >
                Get In Touch
              </Link>
              <button
                onClick={() => setOpen(!open)}
                data-cursor="hover"
                data-testid="navbar-menu-toggle"
                className="lg:hidden w-10 h-10 rounded-full border border-[#E7E9E4]/15 hover:border-[#6A8F3E] flex items-center justify-center text-[#E7E9E4] transition-colors"
                aria-label={open ? 'Close menu' : 'Open menu'}
              >
                {open ? <X size={18} /> : <Menu size={18} />}
              </button>
            </div>
          </nav>
          </div>
        </div>
      </header>

      {/* Mobile / Full menu — solid fill, no backdrop-blur (Safari drops the sheet). */}
      <div
        className={`fixed inset-0 z-[45] bg-[#162A1E] overflow-y-auto overscroll-contain ${open ? 'pointer-events-auto' : 'pointer-events-none invisible'}`}
        aria-hidden={!open}
      >
        <div className="min-h-full flex flex-col justify-start md:justify-center px-8 md:px-16 pt-28 pb-16 md:pt-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 w-full max-w-[1400px] mx-auto">
            <ul className="md:col-span-7 space-y-1">
              {NAV.map((item) => (
                <li key={item.href}>
                  <Link to={item.href} className="block font-serif-editorial text-[2rem] sm:text-4xl md:text-7xl text-[#E7E9E4] hover:text-[#6A8F3E] transition-colors py-1.5">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="md:col-span-4 md:col-start-9 space-y-8">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#6A8F3E] mb-3">Direct Line</div>
                <a href={`tel:${BRAND.phoneTel}`} className="text-2xl text-[#E7E9E4] hover:text-[#6A8F3E] transition-colors">{BRAND.phone}</a>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#6A8F3E] mb-3">Email</div>
                <a href={`mailto:${BRAND.email}`} className="text-base text-[#E7E9E4] hover:text-[#6A8F3E] transition-colors break-all">{BRAND.email}</a>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#6A8F3E] mb-3">Coverage</div>
                <div className="text-base text-[#E7E9E4]">{BRAND.area}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
